import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HtmlEditor, { Toolbar, Item } from 'devextreme-react/html-editor';
import { ValueChangedEvent } from 'devextreme/ui/html_editor';
import { SnackbarMessage, useSnackbar } from "../../hooks";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import "./StatusDetailsDialog.scss";
import {
  ProjectsCustomizationDataRequestDto,
  ProjectsCustomizationDataDto,
} from "../../api-client-nswag/create-sites-client";

const getStatusNotesHtml = (response: any): string => {
  const notesFromNestedData = response?.data?.data?.notes;
  if (typeof notesFromNestedData === "string") {
    return notesFromNestedData;
  }

  const notesFromData = response?.data?.notes;
  if (typeof notesFromData === "string") {
    return notesFromData;
  }

  if (typeof response?.data === "string") {
    return response.data;
  }

  if (typeof response === "string") {
    return response;
  }

  return "";
};

interface StatusDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  projectName: string;
  projectIdMdm: number;
  siteId: number;
  isClientView: boolean;
  canUpdateNotes?: boolean;
  currentStatus: "New" | "Ready to Start" | "In Progress" | "On Hold" | "Completed";
  onStatusUpdate?: (newStatus: string, notes: string) => void;
}

const StatusDetailsDialog: React.FC<StatusDetailsDialogProps> = ({
  open,
  onClose,
  projectName,
  projectIdMdm,
  siteId,
  isClientView,
  canUpdateNotes,
  currentStatus,
  onStatusUpdate,
}) => {
 
   const [notes, setNotes] = useState<string>("");
  const [notesLimitError, setNotesLimitError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorKey, setEditorKey] = useState<number>(0);
  const currentNotesRef = useRef<string>("");
  const isDirtyRef = useRef<boolean>(false);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const STATUS_NOTES_MAX = 255;
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const canWriteNotes = !isClientView && canUpdateNotes !== false;

  // Load status details when dialog opens
  useEffect(() => {
    if (open) {
      loadStatusDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectIdMdm, siteId]);

  const loadStatusDetails = async () => {
    try {
      setLoading(true);
      // Fetch existing notes for the project
      const response: any = await createSitesClientApi.tim_FetchProjectCustomizationDataNotes(
        siteId,
        projectIdMdm
      );

      const existingNotes = getStatusNotesHtml(response);
      setNotes(existingNotes);
      currentNotesRef.current = existingNotes;
      isDirtyRef.current = false;
      setSaveEnabled(false);
      setNotesLimitError(false);
      // Force editor to remount with new notes
      setEditorKey(prev => prev + 1);
    } catch (error) {
      console.error("Failed to load status details:", error);
      showSnackbar({
        message: SnackbarMessage.FAILED_TO_LOAD_STATUS_DETAILS,
        severity: "error",
      });
      // Set empty notes if fetch fails
      setNotes("");
      currentNotesRef.current = "";
      isDirtyRef.current = false;
      setSaveEnabled(false);
      setNotesLimitError(false);
      setEditorKey(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = useCallback((e: ValueChangedEvent) => {
    if (!canWriteNotes) return;
    const val = e.value || "";
    const tmp = document.createElement('div');
    tmp.innerHTML = val;
    const plain = tmp.textContent || tmp.innerText || "";
    // Allow the overflow to be typed and just surface a validation message instead of blocking input
    currentNotesRef.current = val;
    setNotesLimitError(plain.length > STATUS_NOTES_MAX);
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      setSaveEnabled(true);
    }
  }, [canWriteNotes]);

  const notesEditor = useMemo(() => (
    <HtmlEditor
      key={editorKey}
      defaultValue={notes}
      onValueChanged={handleNotesChange}
      height={200}
      disabled={saving}
    >
      <Toolbar>
        <Item name="size" acceptedValues={["10pt", "12pt", "14pt", "16pt", "18pt", "20pt", "24pt"]} />
        <Item name="separator" />
        <Item name="bold" />
        <Item name="italic" />
        <Item name="underline" />
        <Item name="separator" />
        <Item name="alignLeft" />
        <Item name="alignCenter" />
        <Item name="alignRight" />
        <Item name="separator" />
        <Item name="orderedList" />
        <Item name="bulletList" />
      </Toolbar>
    </HtmlEditor>
  ), [editorKey, notes, saving, handleNotesChange]);

  const handleSave = async () => {
    if (!canWriteNotes) return;
    if (notesLimitError) return;
    try {
      setSaving(true);

       // Build the API payload using the ref value
      const projectData: ProjectsCustomizationDataDto = {
        projectIdMDM: projectIdMdm,
        projectCustomName: undefined,
        isVisible: undefined,
        entitiesToHide: undefined,
        deliverablesToHide: undefined,
        engamentManagersToHide: undefined,
        notes: currentNotesRef.current,
      };

      const requestData: ProjectsCustomizationDataRequestDto = {
        hqSiteId: siteId,
        projectsCustomizationDatas: [projectData],
      };

      await createSitesClientApi.tim_ProjectsCustomizationDataUpdate(
        requestData
      );
      
      if (onStatusUpdate) {
        onStatusUpdate(currentStatus, currentNotesRef.current);
      }
      
      showSnackbar({
        message: SnackbarMessage.STATUS_DETAILS_UPDATED,
        severity: "success",
      });
      
      isDirtyRef.current = false;
      setSaveEnabled(false);
      onClose();
    } catch (error) {
      console.error("Failed to save status details:", error);
      showSnackbar({
        message: SnackbarMessage.FAILED_TO_SAVE_STATUS_DETAILS,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!canWriteNotes) {
      onClose();
      return;
    }
    if (isDirtyRef.current) {
      setIsCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleCancelConfirmClose = () => {
    setIsCancelConfirmOpen(false);
  };

  const handleExitWithoutSaving = () => {
    isDirtyRef.current = false;
    setSaveEnabled(false);
    setIsCancelConfirmOpen(false);
    onClose();
  };

  const handleSaveFromConfirm = async () => {
    setIsCancelConfirmOpen(false);
    await handleSave();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      className="status-details-dialog"
    >
      <DialogTitle className="dialog-title-container">
        <div className="title-content">
           <div className={`title-text${isClientView ? " client-view-title" : ""}`}>
             {isClientView && <InfoOutlinedIcon className="status-details-info-icon" />}
              <span>Status Details</span>
          </div>  
          {canWriteNotes && (
            <div className="subtitle-text">View and update Status Details for the selected Project.</div>
          )}
        </div>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          className="close-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="dialog-content">
        {loading ? (
          <div className="loading-container">
            <CircularProgress />
            <span>Loading status details...</span>
          </div>
        ) : (
          <>
            <div className="status-field-section">
              <label className="field-label">Status</label>
              <div className="status-display">
                <Chip 
                  label={currentStatus} 
                  className={`chip status ${String(currentStatus)
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  size="small"
                  variant="outlined"
                />

                {currentStatus === "Ready to Start" && (
                  <div className="ready-to-start-note">
                    <InfoOutlinedIcon className="ready-to-start-note-icon" />
                    <span className="ready-to-start-note-text">
                      Project planning is complete.
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="notes-section">
              <label className="field-label">Status Detail Notes</label>              
              {!canWriteNotes ? (
                <div
                  className="existing-notes-content"
                  dangerouslySetInnerHTML={{
                    __html: notes?.trim() ? notes : "",
                  }}
                />
              ) : (
                <div className="notes-editor">
                  {notesEditor}
                </div>
              )}
              {canWriteNotes && notesLimitError && (
                <div className="notes-limit-error">
                  The entered value cannot exceed 255 characters.
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className="dialog-actions">
        {!canWriteNotes ? (
          <Button
            onClick={handleCancel}
            className="save-button"
            variant="contained"
          >
            Close
          </Button>
        ) : (
          <>
            <Button 
              onClick={handleCancel}
              disabled={saving}
              className="cancel-button"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving || !saveEnabled || notesLimitError}
              variant="contained"
              className="save-button"
            >
              {saving ? <CircularProgress size={20} /> : "Save Changes"}
            </Button>
          </>
        )}
      </DialogActions>

      <Dialog
        open={isCancelConfirmOpen}
        onClose={handleCancelConfirmClose}
        maxWidth="sm"
        fullWidth
        className="cancel-confirm-dialog"
      >
        <DialogTitle>Are you sure you want to cancel?</DialogTitle>
        <DialogContent>
          You have unsaved changes. Leaving now will discard them.
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleExitWithoutSaving}
            disabled={saving}
          >
            Exit
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveFromConfirm}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default StatusDetailsDialog;
